import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import PanToolIcon from '@material-ui/icons/PanTool';
import ScreenShareIcon from '@material-ui/icons/ScreenShare';
import VideocamIcon from '@material-ui/icons/Videocam';
import VideocamOffIcon from '@material-ui/icons/VideocamOff';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import withStyles from '@material-ui/styles/withStyles';
import get from 'lodash/get';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendMessageAction } from 'store/message/messageActions';
import * as videoActions from 'store/video/videoActions';

const RosterIconButton = withStyles(() => ({
  root: {
    padding: 3,
  },
}))(IconButton);

const useStyles = makeStyles(() => ({
  notAllowed: {
    cursor: 'not-allowed',
  },
}));

Roster.propTypes = {
  roster: PropTypes.object.isRequired,
};

export default function Roster({ roster }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    classMode,
    isSharingContent,
    meetingId,
    name,
    attendeeId,
    hostId,
    isHost,
    audioVideo,
    meetingSession,
    videoAttendees,
  } = useSelector(({ user, video, chime }) => ({
    classMode: user.classMode,
    isSharingContent: video.isSharingContent,
    meetingId: chime.meetingId,
    attendeeId: chime.attendeeId,
    name: chime.name,
    hostId: chime.hostId,
    isHost: chime.isHost,
    audioVideo: chime.audioVideo,
    meetingSession: chime.meetingSession,
    videoAttendees: video.tileMap,
  }));

  const [filterRoster, setFilterRoster] = useState({ search: '', raisedOnly: false });

  const updateFilterRoster = (obj) => setFilterRoster({ ...filterRoster, ...obj });

  let attendeeIds;
  if (meetingSession && roster) {
    attendeeIds = Object.keys(roster).filter((thisAttendeeId) => {
      return !!roster[thisAttendeeId].name;
    });
  }

  const handleMuteParticipant = async (participantAttendeeId) => {
    const attendee = roster[participantAttendeeId];
    if (attendee?.muted) {
      if (participantAttendeeId === attendeeId) {
        audioVideo.realtimeUnmuteLocalAudio();
      }
    } else if (!attendee?.muted) {
      if (participantAttendeeId === attendeeId) {
        audioVideo.realtimeMuteLocalAudio();
      } else {
        dispatch(
          sendMessageAction({
            type: 'mute',
            payload: {
              attendeeId: participantAttendeeId,
              meetingId,
              name,
            },
          })
        );
      }
    }
  };

  const selectDeviceStream = async () => {
    let captureStream = null;
    const displayMediaOptions = {
      video: {
        cursor: 'always',
      },
      audio: true,
    };
    try {
      captureStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
      await audioVideo.startContentShare(captureStream);
    } catch (err) {
      console.error('Error: ' + err);
    }
    return captureStream;
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <Button
        color="primary"
        variant="contained"
        fullWidth
        size="small"
        onClick={() => updateFilterRoster({ raisedOnly: !filterRoster.raisedOnly })}
      >
        {filterRoster.raisedOnly ? 'Show All' : 'Show Raised'}
      </Button>
      {(classMode === 'Teacher' || isHost) && (
        <Button
          fullWidth
          disabled={!(classMode === 'Teacher' || isHost)}
          color="secondary"
          variant="contained"
          size="small"
          onClick={() =>
            dispatch(
              sendMessageAction({
                type: 'mute-all',
                payload: {
                  attendeeId,
                  meetingId,
                  name,
                },
              })
            )
          }
        >
          Mute All
        </Button>
      )}
      {(classMode === 'Teacher' || isHost) && (
        <Button
          fullWidth
          disabled={!(classMode === 'Teacher' || isHost)}
          color="secondary"
          variant="contained"
          size="small"
          onClick={() =>
            dispatch(
              sendMessageAction({
                type: 'unmute-all',
                payload: {
                  attendeeId,
                  meetingId,
                  name,
                },
              })
            )
          }
        >
          UnMute All
        </Button>
      )}
      <Box style={{ marginTop: 10 }}>
        <OutlinedInput
          style={{ height: 30 }}
          fullWidth
          placeholder="Search Roster"
          onChange={(e) => updateFilterRoster({ search: e.target.value })}
        />
      </Box>
      {attendeeIds &&
        attendeeIds
          .filter((participantAttendeeId) => {
            const isRaised = get(roster, `${participantAttendeeId}.raisedHand.isRaised`, false);
            if (filterRoster.raisedOnly && !isRaised) return false;
            if (!filterRoster.search || !roster[participantAttendeeId]?.name) {
              return participantAttendeeId;
            }
            return roster[participantAttendeeId].name
              .toLowerCase()
              .match(`^${filterRoster.search.toLowerCase()}.*`);
          })
          .sort((attendeeIdA, attendeeIdB) => {
            const valueA = get(roster, `${attendeeIdA}.raisedHand.time`, null);
            const valueB = get(roster, `${attendeeIdB}.raisedHand.time`, null);

            if (!valueA && valueB) {
              return 1;
            } else if (valueA && !valueB) {
              return -1;
            } else if (!valueA && !valueB) {
              return 0;
            }
            const dateA = moment(valueA);
            const dateB = moment(valueB);
            if (dateA.isBefore(dateB)) {
              return -1;
            } else if (dateB.isBefore(dateA)) {
              return 1;
            } else {
              return 0;
            }
          })
          .map((participantAttendeeId) => {
            const rosterAttendee = roster[participantAttendeeId];
            return (
              <Box
                key={participantAttendeeId}
                display="flex"
                flexWrap="wrap"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                style={{ border: '2 solid black' }}
              >
                {get(roster, `${participantAttendeeId}.raisedHand.isRaised`, null) && (
                  <div>
                    <RosterIconButton
                      style={{ color: 'gold' }}
                      onClick={() =>
                        dispatch(
                          sendMessageAction({
                            type: 'setGuestSpeaker',
                            payload: {
                              attendeeId: participantAttendeeId,
                              meetingId,
                              name,
                            },
                          })
                        )
                      }
                      disabled={classMode !== 'Teacher' || !isHost}
                    >
                      <PanToolIcon />
                    </RosterIconButton>
                  </div>
                )}

                <div>
                  <RosterIconButton
                    onClick={() => dispatch(videoActions.toggleVideoAction())}
                    disabled={participantAttendeeId !== attendeeId}
                  >
                    {videoAttendees[participantAttendeeId]?.isActive ? (
                      <VideocamIcon />
                    ) : (
                      <VideocamOffIcon />
                    )}
                  </RosterIconButton>
                </div>

                <div>
                  <RosterIconButton
                    onClick={() => handleMuteParticipant(participantAttendeeId)}
                    disabled={
                      !(classMode === 'Teacher' || isHost || participantAttendeeId === attendeeId)
                    }
                  >
                    {!rosterAttendee.muted ? (
                      <VolumeUpIcon />
                    ) : (
                      <VolumeOffIcon
                        className={participantAttendeeId === attendeeId ? '' : classes.notAllowed}
                      />
                    )}
                  </RosterIconButton>
                </div>

                {(classMode === 'Teacher' || isHost) && name !== rosterAttendee?.name && (
                  <div>
                    <RosterIconButton
                      onClick={() =>
                        dispatch(
                          sendMessageAction({
                            type: 'remove',
                            payload: {
                              attendeeId: participantAttendeeId,
                              meetingId,
                              name,
                            },
                          })
                        )
                      }
                      disabled={!(classMode === 'Teacher' || isHost)}
                    >
                      <CancelIcon />
                    </RosterIconButton>
                  </div>
                )}

                {name === rosterAttendee?.name && (
                  <RosterIconButton
                    onClick={() => selectDeviceStream()}
                    disabled={isSharingContent}
                  >
                    <ScreenShareIcon />
                  </RosterIconButton>
                )}
                <div>
                  {rosterAttendee.externalUserId === hostId
                    ? `${rosterAttendee.name} (host)`
                    : `${rosterAttendee.name}`}
                </div>
              </Box>
            );
          })}
      <Box flexGrow={1} />
    </Box>
  );
}
